import { ComponentType } from 'react';
import {
  Home, TrendingUp, Building2, CircleUser as UserCircle, CalendarClock, GitMerge, Smartphone,
  Layers, UserCog, Book, Mail, CalendarDays, MessageSquare, MessageCircle, Inbox,
  User, Kanban, Zap, Eye, Megaphone, ClipboardList,
  Plug, Users, Code2, ListChecks, Clock, Settings,
  Rocket, CreditCard, Database, Link2, Bell, PenTool, Package, Briefcase,
  Send, LayoutTemplate, UsersRound, BarChart3, StickyNote, FileText, Lightbulb, MapPin, Compass,
  Activity, Sparkles, Network, ListTodo, BellRing, PenLine, Gauge, ShieldCheck, Presentation, Target, FileSignature
} from 'lucide-react';

export type NavSection = 'dashboard' | 'inbox' | 'relations' | 'travail' | 'equipe' | 'autonomie' | 'crm' | 'onboarding' | 'contenu' | 'newsletter' | 'produit' | 'notes' | 'settings';

export interface NavItem {
  name: string;
  href: string;
  icon: ComponentType<{ className?: string }>;
  dividerBefore?: boolean;
}

export interface SectionConfig {
  id: NavSection;
  label: string;
  icon: ComponentType<{ className?: string }>;
  href: string;
  pathPrefixes: string[];
  sidebarItems: NavItem[];
  showInHeader?: boolean; // default true
  /** What the tool is for, in the « Outils » menu of the Bureau (PLAN-BUREAU lot 3). */
  description?: string;
  sidebarWidth?: string; // CSS class, default 'w-52'
  fullBleedContent?: boolean; // if true, remove py-6 px-8 padding on main content
}

export interface SettingsGroup {
  label: string;
  items: NavItem[];
}

export const SECTIONS: SectionConfig[] = [
  {
    id: 'dashboard',
    label: 'Tableau de bord',
    icon: Home,
    href: '/admin/tableau-de-bord',
    pathPrefixes: ['/admin/tableau-de-bord', '/admin/dashboard', '/admin/overview', '/admin/statistics'],
    description: 'MRR, pipeline, usage',
    sidebarItems: [],
    showInHeader: false,
  },
  {
    id: 'inbox',
    label: 'Messagerie',
    icon: Inbox,
    description: 'Conversations des clients dans l’app',
    href: '/admin/inbox',
    pathPrefixes: ['/admin/inbox'],
    sidebarItems: [],
    sidebarWidth: 'w-0',
    fullBleedContent: true,
  },
  {
    id: 'relations',
    label: 'Relations',
    icon: Network,
    description: 'Mails, appels et SMS par compte',
    href: '/admin/relations',
    pathPrefixes: ['/admin/relations'],
    sidebarItems: [],
    sidebarWidth: 'w-0',
    fullBleedContent: true,
  },
  {
    id: 'travail',
    label: 'Bureau',
    icon: ListTodo,
    description: 'Tes missions avec Otto',
    href: '/admin/travail',
    pathPrefixes: ['/admin/travail'],
    sidebarItems: [],
    sidebarWidth: 'w-0',
    fullBleedContent: true,
  },
  {
    id: 'equipe',
    label: 'Équipe',
    icon: Users,
    description: 'Les rôles d’Otto, leur planning, recruter',
    href: '/admin/equipe',
    pathPrefixes: ['/admin/equipe'],
    sidebarItems: [],
    sidebarWidth: 'w-0',
    fullBleedContent: true,
  },
  {
    id: 'autonomie',
    label: 'Autonomie',
    icon: Gauge,
    description: 'Ce qu’Otto fait seul, et jusqu’où',
    href: '/admin/autonomie',
    pathPrefixes: ['/admin/autonomie'],
    sidebarItems: [],
    sidebarWidth: 'w-0',
    fullBleedContent: true,
  },
  {
    id: 'crm',
    label: 'CRM',
    icon: Briefcase,
    description: 'Comptes, contacts, opportunités, événements, facturation, business case',
    href: '/admin/crm/opportunities',
    pathPrefixes: ['/admin/crm', '/admin/appointments', '/admin/revenue', '/admin/facturation', '/admin/business-case', '/admin/evenements', '/admin/robot'],
    sidebarItems: [
      { name: 'Revenue', href: '/admin/revenue', icon: BarChart3 },
      { name: 'Facturation', href: '/admin/facturation', icon: CreditCard },
      { name: 'Business case', href: '/admin/business-case', icon: Target },
      { name: 'Opportunités', href: '/admin/crm/opportunities', icon: TrendingUp },
      { name: 'Comptes', href: '/admin/crm/accounts', icon: Building2 },
      { name: 'Carte', href: '/admin/crm/carte', icon: MapPin },
      { name: 'Contacts', href: '/admin/crm/contacts', icon: UserCircle },
      { name: 'Rendez-vous', href: '/admin/appointments', icon: CalendarClock },
      { name: 'Événements', href: '/admin/evenements', icon: Presentation },
      { name: 'Conditions robot', href: '/admin/robot/conditions', icon: FileSignature },
      { name: 'Doublons', href: '/admin/crm/duplicates', icon: GitMerge, dividerBefore: true },
      { name: 'Sync Google', href: '/admin/crm/google-sync', icon: Smartphone },
    ],
  },
  {
    id: 'onboarding',
    label: 'Onboarding',
    icon: Rocket,
    description: 'Démarrage des nouveaux clients',
    href: '/admin/onboarding/leads',
    pathPrefixes: ['/admin/onboarding', '/admin/client-accounts'],
    sidebarItems: [
      { name: 'Clients', href: '/admin/onboarding/leads', icon: Layers },
      { name: 'Instances', href: '/admin/onboarding/instances', icon: Layers },
      { name: 'Template par défaut', href: '/admin/onboarding/template', icon: Settings },
      { name: 'Comptes Clients', href: '/admin/client-accounts', icon: UserCog },
    ],
  },
  {
    id: 'contenu',
    label: 'Contenu',
    icon: PenTool,
    description: 'Base de connaissance, calendrier éditorial (blog et LinkedIn), personas',
    href: '/admin/kb/articles',
    pathPrefixes: ['/admin/kb', '/admin/linkedin', '/admin/comments', '/admin/personas'],
    sidebarItems: [
      { name: 'Base de connaissances', href: '/admin/kb/articles', icon: Book },
      { name: 'Calendrier éditorial', href: '/admin/linkedin', icon: CalendarDays },
      { name: 'Personas et preuves', href: '/admin/personas', icon: UsersRound },
      { name: 'Commentaires', href: '/admin/comments', icon: MessageSquare },
    ],
  },
  {
    id: 'newsletter',
    label: 'Newsletter',
    icon: Mail,
    description: 'Campagnes et abonnés',
    href: '/admin/newsletter',
    pathPrefixes: ['/admin/newsletter'],
    sidebarItems: [
      { name: 'Campagnes', href: '/admin/newsletter', icon: Send },
      { name: 'Templates', href: '/admin/newsletter/templates', icon: LayoutTemplate },
      { name: 'Abonnés', href: '/admin/newsletter/subscribers', icon: UsersRound },
      { name: 'Dashboard', href: '/admin/newsletter/dashboard', icon: BarChart3 },
      { name: 'Analytics', href: '/admin/newsletter/analytics', icon: BarChart3 },
    ],
  },
  {
    id: 'produit',
    label: 'Produit',
    icon: Package,
    description: 'Backlog, versions, annonces, widget, cockpit IA',
    href: '/admin/my-dashboard',
    pathPrefixes: [
      '/admin/my-dashboard', '/admin/backlog', '/admin/feature-requests',
      '/admin/versions', '/admin/features', '/admin/roadmap-preview',
      '/admin/feature-announcements', '/admin/feature-analytics',
      '/admin/client-announcements', '/admin/client-surveys',
      '/admin/widget-tours', '/admin/widget-badges', '/admin/product-intelligence',
      '/admin/cockpit-ia', '/admin/qualite',
    ],
    sidebarItems: [
      { name: 'Product Intelligence', href: '/admin/product-intelligence', icon: Activity },
      { name: 'Mon Backlog', href: '/admin/my-dashboard', icon: User },
      { name: 'Backlog Agile', href: '/admin/backlog', icon: Kanban },
      { name: 'Demandes clients', href: '/admin/feature-requests', icon: Lightbulb },
      { name: 'Annonces clients', href: '/admin/client-announcements', icon: Megaphone },
      { name: 'Visites guidées', href: '/admin/widget-tours', icon: Compass },
      { name: 'Pastilles Nouveautés', href: '/admin/widget-badges', icon: Sparkles },
      { name: 'Enquêtes', href: '/admin/client-surveys', icon: ClipboardList },
      { name: 'Cockpit IA', href: '/admin/cockpit-ia', icon: Activity },
      { name: 'Qualité', href: '/admin/qualite', icon: ShieldCheck },
      { name: 'Versions', href: '/admin/versions', icon: Zap },
      { name: 'Roadmap Publique', href: '/admin/roadmap-preview', icon: Eye },
      { name: 'Annonces Features', href: '/admin/feature-announcements', icon: Megaphone },
    ],
  },
  {
    id: 'notes',
    label: 'Notes',
    icon: StickyNote,
    description: 'Tes notes et mémos vocaux',
    href: '/admin/notes',
    pathPrefixes: ['/admin/notes'],
    sidebarItems: [],
    showInHeader: false,
    sidebarWidth: 'w-[280px]',
    fullBleedContent: true,
  },
];

/** The Bureau's home section, always first in the header (PLAN-BUREAU lot 3). */
export const HOME_SECTION: NavSection = 'travail';

/** Always in the header, after the Bureau and before the pinned tools (mock-up « Équipe »). */
export const FIXED_SECTIONS: readonly NavSection[] = [HOME_SECTION, 'equipe'];

/** The sections that draw Encre's sidebar instead of the header (PLAN-BUREAU § 4 quater). */
export const ENCRE_SECTIONS: readonly NavSection[] = [HOME_SECTION, 'equipe'];

/** The tools of the « Outils » menu, in its order (mock-up « Navigation »); pinned ones join the header. */
export const TOOL_SECTIONS: readonly NavSection[] = [
  'relations', 'crm', 'inbox', 'onboarding', 'contenu', 'newsletter', 'produit', 'autonomie', 'dashboard', 'notes',
];

/** Pinned until Gilles says otherwise: the mock-up pins Relations. */
export const DEFAULT_PINNED_TOOLS: readonly NavSection[] = ['relations'];

export const SETTINGS_GROUPS: SettingsGroup[] = [
  {
    label: 'Général',
    items: [
      { name: 'Entreprise et équipe', href: '/admin/settings/organization', icon: Building2 },
      { name: 'Paramètres & Intégrations', href: '/admin/settings/company', icon: Plug },
      { name: 'Utilisateurs', href: '/admin/settings/users', icon: Users },
      { name: 'API Management', href: '/admin/settings/api', icon: Code2 },
    ],
  },
  {
    label: 'Rendez-vous',
    items: [
      { name: 'Types de RDV', href: '/admin/settings/appointment-types', icon: ListChecks },
      { name: 'Disponibilités', href: '/admin/settings/appointment-availability', icon: Clock },
      { name: 'Paramètres', href: '/admin/settings/appointment-settings', icon: Settings },
    ],
  },
  {
    label: 'Contenu',
    items: [
      { name: 'Comptes-rendus', href: '/admin/settings/meeting-reports', icon: FileText },
      { name: 'Catégories KB', href: '/admin/settings/kb-categories', icon: Book },
      { name: 'Templates Notifications', href: '/admin/settings/notification-templates', icon: Bell },
    ],
  },
  {
    label: 'Technique',
    items: [
      { name: 'Produits Stripe', href: '/admin/settings/stripe-products', icon: CreditCard },
      { name: 'Événements Stripe', href: '/admin/settings/stripe-events', icon: CreditCard },
      { name: 'Indexation', href: '/admin/settings/indexation', icon: Database },
      { name: 'Liens brisés', href: '/admin/settings/broken-links', icon: Link2 },
      { name: 'Centre Notifications', href: '/admin/settings/notification-center', icon: Bell },
      { name: 'Mes notifications', href: '/admin/settings/notification-preferences', icon: BellRing },
      { name: 'Appareils push (PWA)', href: '/admin/settings/devices', icon: Bell },
      { name: 'Widget Otto', href: '/admin/settings/widget-config', icon: MessageCircle },
      { name: 'Skills Otto', href: '/admin/settings/skills', icon: Sparkles },
      { name: 'Mémoire de communication', href: '/admin/settings/writing-profile', icon: PenLine },
    ],
  },
];

export function getSectionFromPath(pathname: string): NavSection {
  if (pathname.startsWith('/admin/settings')) return 'settings';

  for (const section of SECTIONS) {
    if (section.id === 'dashboard') continue;
    if (section.pathPrefixes.some(prefix => pathname.startsWith(prefix))) {
      return section.id;
    }
  }

  return 'dashboard';
}
